import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Flag } from "lucide-react";
import api from "@/lib/api";
import ProgressCharts from "@/components/platform/ProgressCharts";
import { Empty, TabHeading, fmtDate } from "./shared";

const COLS = [
  { key: "record_type", label: "Type" },
  { key: "ten_yard", label: "10yd" },
  { key: "flying_ten", label: "Fly10" },
  { key: "forty_yard", label: "40yd" },
  { key: "vertical", label: "Vert" },
  { key: "broad", label: "Broad" },
  { key: "speed_score", label: "Speed" },
  { key: "power_score", label: "Power" },
];

export default function TestingTab({ athleteId, refreshKey, canEdit }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/test-records?athlete_id=${athleteId}`);
      setRecords(data);
    } catch { toast.error("Failed to load testing data."); } finally { setLoading(false); }
  }, [athleteId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const del = async (id) => {
    try { await api.delete(`/test-records/${id}`); toast.success("Record deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  if (loading) return <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>;

  return (
    <div data-testid="af-testing">
      <TabHeading>Testing Center</TabHeading>
      {records.length === 0 ? (
        <Empty testid="af-testing-empty">No test records yet. Use Quick Actions to add an evaluation or retest.</Empty>
      ) : (
        <>
          <div className="overflow-x-auto border border-white/10 bg-[#121212] mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-white/40">
                  <th className="text-left font-mono-accent text-[10px] tracking-[0.2em] uppercase px-4 py-3">Date</th>
                  {COLS.map((c) => <th key={c.key} className="text-left font-mono-accent text-[10px] tracking-[0.2em] uppercase px-3 py-3">{c.label}</th>)}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {records.slice().reverse().map((r) => (
                  <tr key={r.id} data-testid={`af-test-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 whitespace-nowrap text-white/80">{fmtDate(r.date)} {r.is_baseline && <Flag size={11} className="inline text-white/60 ml-1" />}</td>
                    {COLS.map((c) => <td key={c.key} className="px-3 py-3 text-white/70 whitespace-nowrap">{r[c.key] ?? "—"}</td>)}
                    <td className="px-3 py-3 text-right">
                      <button data-testid={`af-test-del-${r.id}`} onClick={() => del(r.id)} className="text-white/30 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ProgressCharts records={records} />
        </>
      )}
    </div>
  );
}
